import { Ref, forwardRef, useMemo } from "react"
import { Formik, FormikProps } from "formik"
import * as yup from "yup"
import { StandardFormikErrorScrollWrapper } from "../../../../../../shared/components/forms/StandardFormikErrorScrollWrapper"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { BillReceiver, BillReceiverEndpointType } from "../settingsTypes"
import { EditBillReceiverFormContent } from "./EditBillReceiverFormContent"
import { CountryCode, BillReceiversType } from "../../../../../../shared/components/billReceivers/billReceiverTypes"
import {
    CVR_LENGTH,
    EAN_LENGTH,
    MAX_EMAIL_LENGTH,
    MAX_NAME_LENGTH,
    POSTAL_CODE_REGEX,
} from "../../../../../../constants/inputValidationDefaults"

export type EditBillReceiverFormValues = {
    billType: BillReceiversType
    debtorNumber: string
    name: string
    country: string
    addressLine1: string
    addressLine2: string
    postalCode: string
    city: string
    telephone: string
    email: string
    contactPerson: string
    vatnumber: string
    endpointType: BillReceiverEndpointType
    endpointValue: string
    isActive: boolean
}

type EditBillReceiverFormProps = Readonly<{
    billReceiver: BillReceiver | null
    onSubmit: (formData: EditBillReceiverFormValues) => Promise<void>
}>

type FormikRef = Ref<FormikProps<EditBillReceiverFormValues>>

export const EditBillReceiverForm = forwardRef(
    ({ billReceiver, onSubmit }: EditBillReceiverFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()

        const initialValues: EditBillReceiverFormValues | null = useMemo(() => {
            if (!billReceiver) return null

            const addressLines = billReceiver.address?.addressLines || []
            const billType = billReceiver.type as BillReceiversType

            // Determine endpoint type based on bill receiver type
            const getEndpointType = (): BillReceiverEndpointType => {
                if (billType === BillReceiversType.PUBLIC_INSTITUTION) {
                    return BillReceiverEndpointType.EAN
                }
                if (billType === BillReceiversType.PRIVATE_PERSON) {
                    return BillReceiverEndpointType.EMAIL
                }
                // Private company: use API value
                return billReceiver.endpoint.type
            }

            return {
                billType,
                debtorNumber: billReceiver.debitorNo || "",
                name: billReceiver.name,
                country: billReceiver.address?.country?.countryCode || CountryCode.DA,
                addressLine1: addressLines[0] || "",
                addressLine2: addressLines[1] || "",
                postalCode: billReceiver.address?.postalCode || "",
                city: billReceiver.address?.city || "",
                telephone: billReceiver.contactDetails?.phoneNumber || "",
                email: billReceiver.contactDetails?.email || "",
                contactPerson: billReceiver.contactDetails?.name || "",
                vatnumber: billReceiver.vatnumber || "",
                endpointType: getEndpointType(),
                endpointValue:
                    billReceiver.endpoint.type === BillReceiverEndpointType.EAN
                        ? billReceiver.endpoint.ean || ""
                        : billReceiver.endpoint.email || "",
                isActive: billReceiver.isActive,
            }
        }, [billReceiver])

        const requiredMessage = l10n.getString("required-field")
        const invalidEmailMessage = l10n.getString("bill-receivers-validation-email-invalid")
        const postalCodeMessage = l10n.getString("bill-receivers-validation-postal-code-4-digits")
        const telephoneMessage = l10n.getString("bill-receivers-validation-telephone-invalid")
        const cvrMessage = l10n.getString("bill-receivers-validation-cvr-8-digits")
        const eanMessage = l10n.getString("bill-receivers-validation-ean-13-digits")
        const maxLengthMessage = (max: number) => l10n.getString("bill-receivers-validation-max-length", { max })

        const validationSchema = yup.object({
            billType: yup.string().required(requiredMessage),
            name: yup.string().required(requiredMessage).max(MAX_NAME_LENGTH, maxLengthMessage(MAX_NAME_LENGTH)),
            country: yup.string().required(requiredMessage),
            addressLine1: yup.string().required(requiredMessage),
            postalCode: yup.string().required(requiredMessage).matches(POSTAL_CODE_REGEX, postalCodeMessage),
            city: yup.string().required(requiredMessage).max(MAX_NAME_LENGTH, maxLengthMessage(MAX_NAME_LENGTH)),
            telephone: yup
                .string()
                .required(requiredMessage)
                .matches(/^[0-9+\-() ]+$/u, telephoneMessage),
            // Contact person required for public institution and private company
            contactPerson: yup.string().when("billType", {
                is: (billType: BillReceiversType) =>
                    billType === BillReceiversType.PUBLIC_INSTITUTION ||
                    billType === BillReceiversType.PRIVATE_COMPANY,
                then: (schema) =>
                    schema.required(requiredMessage).max(MAX_NAME_LENGTH, maxLengthMessage(MAX_NAME_LENGTH)),
                otherwise: (schema) => schema,
            }),
            // Email required for public institution and private company
            email: yup.string().when("billType", {
                is: (billType: BillReceiversType) =>
                    billType === BillReceiversType.PUBLIC_INSTITUTION ||
                    billType === BillReceiversType.PRIVATE_COMPANY,
                then: (schema) =>
                    schema
                        .required(requiredMessage)
                        .max(MAX_EMAIL_LENGTH, maxLengthMessage(MAX_EMAIL_LENGTH))
                        .email(invalidEmailMessage),
                otherwise: (schema) => schema,
            }),
            // CVR number (vatnumber) required for private company
            vatnumber: yup.string().when("billType", {
                is: BillReceiversType.PRIVATE_COMPANY,
                then: (schema) =>
                    schema.required(requiredMessage).matches(new RegExp(`^\\d{${CVR_LENGTH}}$`, "u"), cvrMessage),
                otherwise: (schema) => schema,
            }),
            endpointType: yup.string().required(requiredMessage),
            // Endpoint value required, validates based on endpoint type
            endpointValue: yup
                .string()
                .required(requiredMessage)
                .test("endpoint-format", "", function (value) {
                    const { endpointType } = this.parent
                    if (endpointType === BillReceiverEndpointType.EMAIL) {
                        if (value && value.length > MAX_EMAIL_LENGTH) {
                            return this.createError({ message: maxLengthMessage(MAX_EMAIL_LENGTH) })
                        }
                        if (!yup.string().email().isValidSync(value)) {
                            return this.createError({ message: invalidEmailMessage })
                        }
                    }
                    if (endpointType === BillReceiverEndpointType.EAN) {
                        if (!new RegExp(`^\\d{${EAN_LENGTH}}$`, "u").test((value || "").trim())) {
                            return this.createError({ message: eanMessage })
                        }
                    }
                    return true
                }),
        })

        if (!initialValues) {
            return null
        }

        return (
            <Formik<EditBillReceiverFormValues>
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
                innerRef={ref}
                validateOnBlur={false}
                validateOnChange={false}
            >
                <StandardFormikErrorScrollWrapper<EditBillReceiverFormValues>
                    children={<EditBillReceiverFormContent />}
                />
            </Formik>
        )
    },
)
